'use client'

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Spinner from '../components/Spinner';
import Breadcrumbs from '../components/Breadcrumbs';
import api from '../api/axios';

// ── Constants ─────────────────────────────────────────────────────────────────

const RESOURCE_LABELS = {
  TEMPLATE:       'Template',
  SENDER:         'Sender',
  CONNECTOR:      'Connector',
  OPT_OUT_POLICY: 'Opt-Out Policy',
  TEAM_MEMBER:    'Team Member',
};

const ACTION_STYLES = {
  CREATE: 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-300',
  UPDATE: 'bg-amber-100  text-amber-800  dark:bg-amber-900/60  dark:text-amber-300',
  DELETE: 'bg-red-100   text-red-800   dark:bg-red-900/60   dark:text-red-300',
};

// Fields that exist in every DTO but add no display value on the detail screen
const SKIP_FIELDS = new Set(['id', 'accountId', '_id', 'createdAt', 'updatedAt']);

const breadcrumbs = [{ name: 'Audit Log', href: '/audit-log' }];

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Convert a camelCase or SCREAMING_SNAKE key to Title Case words.
 * Handles dotted paths from nested flattening (shown with ›).
 */
function formatFieldKey(key) {
  return key
    .split('.')
    .map(part =>
      part
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .trim()
        .replace(/^./, c => c.toUpperCase())
    )
    .join(' › ');
}

/** Stringify a single value cleanly (no raw objects). */
function formatValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean')          return val ? 'Yes' : 'No';
  if (Array.isArray(val))                return val.length ? val.join(', ') : '(empty list)';
  if (typeof val === 'object')           return JSON.stringify(val, null, 2); // last resort
  const s = String(val).trim();
  return s === '' ? '—' : s;
}

/**
 * Recursively flatten a DTO object into { 'dotted.key': 'string value' }.
 * Skips fields in SKIP_FIELDS.
 */
function flattenState(obj, prefix = '') {
  if (!obj || typeof obj !== 'object') return {};
  return Object.entries(obj).reduce((acc, [key, val]) => {
    if (SKIP_FIELDS.has(key)) return acc;
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(acc, flattenState(val, fullKey));
    } else {
      acc[fullKey] = formatValue(val);
    }
    return acc;
  }, {});
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SectionDivider({ label }) {
  return (
    <div className="flex items-center gap-3 my-5">
      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
      <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 min-w-[160px] shrink-0">
      {children}
    </span>
  );
}

/** Option B state diff renderer */
function StateDiff({ action, previousState, newState }) {

  // ── CREATE ─────────────────────────────────────────────────────────────────
  if (action === 'CREATE') {
    const fields = Object.entries(flattenState(newState ?? {}));
    if (fields.length === 0) {
      return <p className="text-sm text-gray-400 mt-4">No state recorded for this event.</p>;
    }
    return (
      <>
        <SectionDivider label="Created with these values" />
        <div className="space-y-2">
          {fields.map(([key, val]) => (
            <div
              key={key}
              className="flex items-start gap-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-4 py-2.5"
            >
              <FieldLabel>{formatFieldKey(key)}</FieldLabel>
              <span className="text-sm text-green-700 dark:text-green-300 font-medium break-all">{val}</span>
            </div>
          ))}
        </div>
      </>
    );
  }

  // ── DELETE ─────────────────────────────────────────────────────────────────
  if (action === 'DELETE') {
    const fields = Object.entries(flattenState(previousState ?? {}));
    if (fields.length === 0) {
      return <p className="text-sm text-gray-400 mt-4">No state recorded for this event.</p>;
    }
    return (
      <>
        <SectionDivider label="Record at time of deletion" />
        <div className="space-y-2">
          {fields.map(([key, val]) => (
            <div
              key={key}
              className="flex items-start gap-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-2.5"
            >
              <FieldLabel>{formatFieldKey(key)}</FieldLabel>
              <span className="text-sm text-red-600 dark:text-red-400 line-through break-all">{val}</span>
            </div>
          ))}
        </div>
      </>
    );
  }

  // ── UPDATE ─────────────────────────────────────────────────────────────────
  const prevFlat    = flattenState(previousState ?? {});
  const nextFlat    = flattenState(newState ?? {});
  const allKeys     = [...new Set([...Object.keys(prevFlat), ...Object.keys(nextFlat)])];
  const changedKeys   = allKeys.filter(k => prevFlat[k] !== nextFlat[k]);
  const unchangedKeys = allKeys.filter(k => prevFlat[k] === nextFlat[k]);

  return (
    <>
      <SectionDivider
        label={
          changedKeys.length === 0
            ? 'No fields changed'
            : `${changedKeys.length} field${changedKeys.length !== 1 ? 's' : ''} changed`
        }
      />

      {changedKeys.length > 0 && (
        <div className="space-y-2">
          {changedKeys.map(key => (
            <div
              key={key}
              className="flex items-center gap-4 flex-wrap rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-4 py-2.5"
            >
              <FieldLabel>{formatFieldKey(key)}</FieldLabel>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="rounded px-2.5 py-0.5 text-xs bg-red-100 dark:bg-red-900/60 text-red-700 dark:text-red-300 line-through font-mono">
                  {prevFlat[key] ?? '—'}
                </span>
                <span className="text-gray-400 dark:text-gray-500 select-none">→</span>
                <span className="rounded px-2.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/60 text-green-700 dark:text-green-300 font-medium font-mono">
                  {nextFlat[key] ?? '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {unchangedKeys.length > 0 && (
        <>
          <SectionDivider label="Unchanged" />
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {unchangedKeys.map(key => (
                <div key={key} className="flex items-center gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-900">
                  <FieldLabel>{formatFieldKey(key)}</FieldLabel>
                  <span className="text-sm text-gray-400 dark:text-gray-500 break-all">{prevFlat[key]}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AuditLogDetail() {
  const { id }          = useParams();
  const navigate        = useNavigate();
  const activeAccountId = useSelector(s => s.globalData.activeAccountId);

  const [event,   setEvent]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!activeAccountId || !id) return;

    const fetchEvent = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(
          `${process.env.REACT_APP_BASE_API_URL}/mvapi/audit/events/${activeAccountId}/${id}`
        );
        setEvent(res.data);
      } catch (err) {
        console.error('AuditLogDetail fetch error:', err);
        setError('Event not found or you do not have access to it.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [activeAccountId, id]);

  // ── Loading / error states ──

  if (loading) return <div className="px-4 sm:px-6 lg:px-8"><Spinner /></div>;

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Breadcrumbs pages={breadcrumbs} />
        <div className="mt-8 text-center py-16">
          <p className="text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={() => navigate('/audit-log')}
            className="mt-4 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            ← Back to Audit Log
          </button>
        </div>
      </div>
    );
  }

  if (!event) return null;

  // ── Render ──

  const resourceLabel = RESOURCE_LABELS[event.resourceType] ?? event.resourceType;
  const actionStyle   = ACTION_STYLES[event.action] ?? 'bg-gray-100 text-gray-600';

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <Breadcrumbs pages={breadcrumbs} />

      <div className="sm:flex sm:items-start sm:gap-4 mb-6">
        <div>
          <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:text-3xl sm:tracking-tight">
            Audit Event
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Full detail for this change record.
          </p>
        </div>
      </div>

      {/* Event header card */}
      <div className="bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">

        {/* Header bar */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${actionStyle}`}>
            {event.action}
          </span>
          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {resourceLabel}
          </span>
          {event.resourceName && (
            <>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {event.resourceName}
              </span>
            </>
          )}
          <div className="sm:ml-auto flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            {event.timestamp && (
              <span>
                🗓 {new Date(event.timestamp).toLocaleString()}
              </span>
            )}
            {event.performedBy && (
              <span>
                👤 {event.performedBy}
              </span>
            )}
          </div>
        </div>

        {/* State diff */}
        <div className="px-6 py-5">
          <StateDiff
            action={event.action}
            previousState={event.previousState}
            newState={event.newState}
          />
        </div>

        {/* Footer — extra metadata */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-gray-400 dark:text-gray-500 mb-0.5 uppercase tracking-wide font-semibold">Event ID</p>
              <p className="text-gray-600 dark:text-gray-400 font-mono break-all">{event.id}</p>
            </div>
            <div>
              <p className="text-gray-400 dark:text-gray-500 mb-0.5 uppercase tracking-wide font-semibold">Resource ID</p>
              <p className="text-gray-600 dark:text-gray-400 font-mono break-all">{event.resourceId ?? '—'}</p>
            </div>
            <div>
              <p className="text-gray-400 dark:text-gray-500 mb-0.5 uppercase tracking-wide font-semibold">Performed by</p>
              <p className="text-gray-600 dark:text-gray-400">{event.performedBy ?? '—'}</p>
            </div>
            <div>
              <p className="text-gray-400 dark:text-gray-500 mb-0.5 uppercase tracking-wide font-semibold">User ID</p>
              <p className="text-gray-600 dark:text-gray-400 font-mono break-all">{event.performedByUserId ?? '—'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={() => navigate('/audit-log')}
          className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
        >
          ← Back to Audit Log
        </button>
      </div>
    </div>
  );
}
